/**
 * The right-click menu on a tracker row.
 *
 * One menu for all three row surfaces -- list, table and board -- because the
 * actions are properties of the *item*, not of how it happens to be drawn, and
 * three menus would be three chances for them to disagree.
 *
 * The rows themselves come from `TrackerActionList` in the shared bundle, so
 * this menu and the item header's overflow menu are the same list of the same
 * commands in the same shape. What is host-specific is only *which* commands a
 * browser tab can honestly offer: it can open, copy, set a status and hand off
 * to the desktop; it cannot open a worktree or start a session.
 *
 * Supplying `onRowContextMenu` to `TrackerGridSurface` is also what restores
 * the table's pinned overflow column. That column shipped switched off because
 * its button dispatches a synthetic `contextmenu` and nothing in the browser
 * was listening -- the listener is now the switch.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  copyTextToClipboard,
  getRecordStatus,
  getRecordTitle,
  NEUTRAL_SWATCH,
  STATUS_COLORS,
  TrackerContextMenu,
  useTrackerCommand,
  useTrackerDataSelector,
  type TrackerContextMenuPoint,
  type TrackerItemAction,
} from '@nimbalyst/collab-bundle/trackers-ui';
import { orgTrackerItemPath } from '../routing';
import { useTrackersShell } from './TrackersShell';
import { isEmbedded } from '../embedded/embedMarker';

interface MenuTarget {
  itemIds: string[];
  point: TrackerContextMenuPoint;
}

export interface TrackerRowMenu {
  /** For `TrackerListView` and the board, which report one row and an event. */
  openForItem: (itemId: string, event: { preventDefault: () => void; clientX: number; clientY: number }) => void;
  /** For `TrackerGridSurface`, which resolves the row -- or the range -- itself. */
  openForRows: (payload: { itemIds: string[]; point: TrackerContextMenuPoint }) => void;
  /** Mount once, beside the view body. */
  element: React.ReactNode;
  /**
   * What the last action did, or why it did nothing. A clipboard write changes
   * nothing on screen and a denied one throws nothing, so without this a copy
   * and a no-op are indistinguishable.
   */
  notice: string | null;
}

const NOTICE_MS = 2400;

export function useTrackerRowMenu(): TrackerRowMenu {
  const { team, projectId, schema, itemPath, navigate } = useTrackersShell();
  const recordsById = useTrackerDataSelector(state => state.recordsById);
  const command = useTrackerCommand();
  const [target, setTarget] = useState<MenuTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const report = useCallback((message: string) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);

  const copyValue = useCallback(async (value: string, copied: string) => {
    try {
      await copyTextToClipboard(value);
      report(copied);
    } catch {
      report('This browser would not let Nimbalyst write to the clipboard.');
    }
  }, [report]);

  const setStatus = useCallback(async (itemIds: readonly string[], field: string, value: string) => {
    try {
      if (itemIds.length === 1) {
        await command({
          type: 'update-item',
          input: { itemId: itemIds[0]!, updates: { [field]: value }, sharing: 'team' },
        });
      } else {
        await command({
          type: 'update-items',
          input: {
            entries: itemIds.map((itemId) => ({
              itemId,
              storeUpdates: { [field]: value },
              sharing: 'team' as const,
            })),
          },
        });
      }
      report(itemIds.length === 1 ? `Set to ${value}` : `${itemIds.length} items set to ${value}`);
    } catch (cause) {
      report(cause instanceof Error ? cause.message : String(cause));
    }
  }, [command, report]);

  const actions = useMemo<TrackerItemAction[]>(() => {
    if (!target) return [];
    const items = target.itemIds
      .map((itemId) => recordsById.get(itemId))
      .filter((item): item is NonNullable<typeof item> => item != null);
    if (items.length === 0) return [];
    const single = items.length === 1 ? items[0]! : null;

    /**
     * A status list only makes sense when every selected row answers to the
     * same workflow. Two types can both call a field `status` and mean
     * different values, so a mixed selection gets no status section rather than
     * one that would write a value half of them do not recognise.
     */
    const types = new Set(items.map((item) => item.primaryType));
    const model = types.size === 1
      ? schema.trackerTypes.find((candidate) => candidate.type === [...types][0])
      : undefined;
    const statusFieldName = model?.roles?.workflowStatus ?? 'status';
    const statusOptions = model?.fields
      .find((field) => field.name === statusFieldName)?.options ?? [];
    const currentStatus = single ? getRecordStatus(single) : null;

    return [
      ...(items.length > 1
        ? [{ id: 'selection', label: `${items.length} items selected`, caption: true }]
        : []),
      ...(single
        ? [{
          id: 'open',
          label: 'Open',
          icon: 'open_in_new',
          onSelect: () => navigate(itemPath(single.id)),
        }]
        : []),
      ...(single
        ? [{
          id: 'copy-link',
          label: 'Copy link',
          icon: 'link',
          onSelect: () => copyValue(
            // Absolute, and without the `?type=`/`?view=` the URL bar carries:
            // that records which list this reader came from, not the item's
            // address. Same rule as the detail header's copy-link.
            new URL(orgTrackerItemPath(team, projectId, single.id), window.location.origin).toString(),
            'Link copied',
          ),
        }]
        : []),
      ...(single?.issueKey
        ? [{
          id: 'copy-key',
          label: 'Copy item key',
          icon: 'tag',
          onSelect: () => copyValue(single.issueKey!, `${single.issueKey} copied`),
        }]
        : []),
      ...(single
        ? [{
          id: 'copy-title',
          label: 'Copy title',
          icon: 'title',
          onSelect: () => copyValue(getRecordTitle(single), 'Title copied'),
        }]
        : []),
      ...(statusOptions.length > 0
        ? [
          { id: 'status-caption', label: 'Set status', caption: true, separatorBefore: true },
          ...statusOptions.map((option) => ({
            id: `status-${option.value}`,
            label: option.label || option.value,
            swatch: STATUS_COLORS[option.value] ?? NEUTRAL_SWATCH,
            // The value it already has is shown, so the menu reads as a state
            // rather than as a list of things that all look equally undone.
            disabled: currentStatus === option.value,
            onSelect: () => setStatus(target.itemIds, statusFieldName, option.value),
          })),
        ]
        : []),
      ...(single && !isEmbedded()
        ? [{
          id: 'open-desktop',
          label: 'Open in desktop',
          icon: 'desktop_windows',
          separatorBefore: true,
          onSelect: () => {
            window.location.href = `nimbalyst://tracker/${encodeURIComponent(single.id)}?orgId=${encodeURIComponent(team.orgId)}`;
          },
        }]
        : []),
    ];
  }, [copyValue, itemPath, navigate, projectId, recordsById, schema.trackerTypes, setStatus, target, team]);

  const openForItem = useCallback((
    itemId: string,
    event: { preventDefault: () => void; clientX: number; clientY: number },
  ) => {
    event.preventDefault();
    setTarget({ itemIds: [itemId], point: { x: event.clientX, y: event.clientY } });
  }, []);

  const openForRows = useCallback((payload: { itemIds: string[]; point: TrackerContextMenuPoint }) => {
    setTarget(payload);
  }, []);

  return {
    openForItem,
    openForRows,
    notice,
    element: (
      <TrackerContextMenu
        point={target?.point ?? null}
        actions={actions}
        onClose={() => setTarget(null)}
        label="Tracker item actions"
      />
    ),
  };
}
